import { Button } from '@startuptools/ui/Button';
import { Box } from '@startuptools/ui/Box';
import { Stack } from '@startuptools/ui/Stack';
import { Typography } from '@startuptools/ui/Typography';
import { Trans } from '@lingui/react';
import { useCompanyId } from '@startuptools/common/react-wrapper';
import { Checkbox } from '@startuptools/ui/Checkbox';
import { ErrorDisplay } from '../../../../components/error-display/ErrorDisplay';
import {
  GqlAgendaItem,
  GqlAgendaItemDecision,
  GqlMeeting,
  GqlMeetingKind,
} from '../../../../graphql/base-types.graphql';
import { useStepper } from '../../../../components/steppers/common/StepperContext';
import { useStepperStepContent } from '../../../../components/steppers/common/StepperStepContentContext';
import { BoardMeetingDocument } from '../../../board-meetings/BoardMeeting.graphql';
import { ShareholdersMeetingDocument } from '../../../shareholders-meetings/ShareholdersMeeting.graphql';
import { useMeetingSetBoardsRecommendationMutation } from './Agenda.graphql';
import { AgendaEdit } from './components/AgendaEdit';
import { AiMcProps } from '../agenda-items/MeetingConcern';
import { GqlMeetingParticipant } from '../../../../graphql/base-types.graphql';
import { DisplayIdentity } from '@startuptools/business-ui/identity/Identity';

export const MeetingStepAgenda = ({
  meeting,
  meetingConcerns,
  agendaItems,
}: {
  meeting: Pick<
    GqlMeeting,
    'id' | 'kind' | 'primaryLang' | 'secondaryLang' | 'boardsRecommendation' | 'companyId' | 'startTime'
  > & {
    meetingParticipants: (Pick<GqlMeetingParticipant, 'id' | 'present' | 'chairman' | 'secretary' | 'adjuster'> & {
      identity: DisplayIdentity;
      proxy?: DisplayIdentity | null;
    })[];
  };
  meetingConcerns: AiMcProps[];
  agendaItems: (Pick<
    GqlAgendaItem,
    | 'id'
    | 'kind'
    | 'firstGroup'
    | 'lastGroup'
    | 'order'
    | 'canHaveBoardsRecommendation'
    | 'commentPrimaryLang'
    | 'commentSecondaryLang'
    | 'mandatory'
    | 'done'
    | 'subjectPrimaryLang'
    | 'subjectSecondaryLang'
  > & {
    agendaItemDecisions: Pick<
      GqlAgendaItemDecision,
      'id' | 'order' | 'decisionPrimaryLang' | 'decisionSecondaryLang'
    >[];
  })[];
}) => {
  const companyId = useCompanyId();
  const { stepNext } = useStepper();
  const { validatorStatus } = useStepperStepContent();
  // eslint-disable-next-line sut/loading-mutation
  const [doSetBoardsStatement] = useMeetingSetBoardsRecommendationMutation({
    refetchQueries: [BoardMeetingDocument, ShareholdersMeetingDocument],
  });

  return (
    <Stack gap={3}>
      <Typography level="h4">
        <Trans id="Agenda" />
      </Typography>

      {meeting.kind !== GqlMeetingKind.BoardMeeting && (
        <Checkbox
          label={<Trans id="Set board's recommendation" />}
          checked={meeting.boardsRecommendation}
          onChange={ev =>
            doSetBoardsStatement({
              variables: { companyId, input: { id: meeting.id, boardsRecommendation: ev.target.checked } },
              optimisticResponse: {
                setMeetingBoardsRecommendation: {
                  id: meeting.id,
                  __typename: 'Meeting',
                  boardsRecommendation: ev.target.checked,
                },
              },
            })
          }
        />
      )}

      <AgendaEdit meeting={meeting} meetingConcerns={meetingConcerns} agendaItems={agendaItems} />

      {validatorStatus?.hasError && <ErrorDisplay error={validatorStatus.error} />}

      <Box>
        <Button onClick={() => stepNext()}>
          <Trans id="Continue" />
        </Button>
      </Box>
    </Stack>
  );
};
